/**
 * Spec 053 Section F (Phase 3), Flow F6 — delivery-fee configuration and the recovery-provider
 * registry. Shapes mirror the orders service's DTOs behind `/api/admin/delivery/**`
 * (gateway route `orders-admin-delivery`, screen `finance`).
 *
 * Two rules this file exists to keep honest:
 *  - `feePerTrip` is what the **customer** pays per Garagat leg, VAT-inclusive (BR-21).
 *  - `costPerTrip` is what **Garagat** pays the provider, ex-VAT, optional for every provider,
 *    and never shown to a customer (BR-26). Blank is a first-class value, not a zero.
 */

/** UAE standard rate; the fee is quoted VAT-inclusive, so net/VAT are extracted 100/105 and 5/105. */
export const DELIVERY_VAT_RATE = 0.05;

/** Flow F6 defaults, shown as the placeholder before an admin has ever saved the config. */
export const DEFAULT_DELIVERY_THRESHOLD = 1000;
export const DEFAULT_DELIVERY_FEE_PER_TRIP = 100;

/** `GET /api/admin/delivery/config`. `updatedBy`/`updatedAt` are null until the first save. */
export interface DeliveryFeeConfig {
  /** Inclusive (BR-21): a service priced exactly at the threshold still carries the fee. */
  threshold: number;
  /** Per Garagat leg, VAT-inclusive. */
  feePerTrip: number;
  /** The actor's Keycloak subject id — never shown raw; see `updatedByName` (#554). */
  updatedBy: string | null;
  /**
   * Display name resolved by identity at read time (#554). Null when the lookup failed or the
   * account is gone, in which case the screen falls back to a short id — the same contract and the
   * same fallback as `AdminActionView.adminName` on the offering history tab.
   */
  updatedByName: string | null;
  updatedAt: string | null;
}

/** `PUT /api/admin/delivery/config`. */
export interface DeliveryFeeConfigInput {
  threshold: number;
  feePerTrip: number;
}

export type RecoveryProviderType = 'CONTRACTOR' | 'IN_HOUSE';

export const RECOVERY_PROVIDER_TYPES: RecoveryProviderType[] = ['CONTRACTOR', 'IN_HOUSE'];

/**
 * Spec 072 v2 — how the car moves: on a recovery truck, or driven by a Zofeur driver. Distinct from
 * `RecoveryProviderType` (who the provider is commercially). A provider has one; an order has the
 * one the customer picked, and only a provider of the same transport type can be assigned to it.
 */
export type TransportType = 'RECOVERY' | 'ZOFEUR';

export const TRANSPORT_TYPES: TransportType[] = ['RECOVERY', 'ZOFEUR'];

/** The admin's words for a transport type, on the registry, the provider form and the order badge. */
export const TRANSPORT_LABEL: Record<TransportType, string> = {
  RECOVERY: 'Recovery truck',
  ZOFEUR: 'Zofeur driver',
};

/** "Zofeur driver" / "Recovery truck"; null for no type (a SELF/SELF order) or an unknown value. */
export function transportLabel(type: TransportType | string | null | undefined): string | null {
  return type === 'RECOVERY' || type === 'ZOFEUR' ? TRANSPORT_LABEL[type] : null;
}

/** The seven emirates, in the order the Zofeur card lists them (`Emirate` in orders). */
export type Emirate =
  | 'DUBAI' | 'ABU_DHABI' | 'SHARJAH' | 'AJMAN' | 'UMM_AL_QUWAIN' | 'RAS_AL_KHAIMAH' | 'FUJAIRAH';

export const EMIRATES: { code: Emirate; label: string }[] = [
  { code: 'DUBAI', label: 'Dubai' },
  { code: 'ABU_DHABI', label: 'Abu Dhabi' },
  { code: 'SHARJAH', label: 'Sharjah' },
  { code: 'AJMAN', label: 'Ajman' },
  { code: 'UMM_AL_QUWAIN', label: 'Umm Al Quwain' },
  { code: 'RAS_AL_KHAIMAH', label: 'Ras Al Khaimah' },
  { code: 'FUJAIRAH', label: 'Fujairah' },
];

/**
 * Spec 080 (#1314) — a Zofeur pricing zone: the seven emirates plus Al Ain, which sits inside Abu Dhabi
 * and is offered whenever Abu Dhabi is (`ZofeurZone` in orders). Zones decide the price only.
 */
export type ZofeurZone = Emirate | 'AL_AIN';

/** Spec 080 — the zone names the card and the server's 422s use. */
export const ZONE_LABELS: Record<ZofeurZone, string> = {
  DUBAI: 'Dubai',
  ABU_DHABI: 'Abu Dhabi',
  SHARJAH: 'Sharjah',
  AJMAN: 'Ajman',
  UMM_AL_QUWAIN: 'Umm Al Quwain',
  RAS_AL_KHAIMAH: 'Ras Al Khaimah',
  FUJAIRAH: 'Fujairah',
  AL_AIN: 'Al Ain',
};

/** Spec 080 — each zone's own whole-AED price; a zone with no key pays the default (Al Ain: Abu Dhabi's first). */
export type ZonePrices = Partial<Record<ZofeurZone, number>>;

/** Spec 080 (DD11) — the server's 422 for one zone's price, word for word. */
export function zonePriceMessage(zone: ZofeurZone): string {
  return `Enter a price for ${ZONE_LABELS[zone]} between AED 1 and AED 10,000, in whole dirhams`;
}

/** Requirements v2 Flow A — what the emirate checkboxes start with before Zofeur was ever configured. */
export const DEFAULT_ZOFEUR_EMIRATES: Emirate[] = ['DUBAI', 'ABU_DHABI', 'SHARJAH'];

/**
 * `GET /api/admin/delivery/zofeur-config` — every field null until the first save.
 *
 * `price` covers pickup AND drop-off, VAT-inclusive, whole dirhams (D21: it splits evenly into the
 * order's two trips). `threshold` null = never free; otherwise the Zofeur trips are free when the
 * service price is above it.
 */
export interface ZofeurConfig {
  price: number | null;
  threshold: number | null;
  emirates: Emirate[] | null;
  /**
   * #1254 — what the Zofeur provider row held in the registry; the leg snapshots it at driver
   * assignment. Optional on the wire for an orders build before #1254. `vatRegistered` null = never
   * stated (a card saved before #1254: Zofeur is not offered until the card is saved again).
   */
  trn?: string | null;
  vatRegistered?: boolean | null;
  /** Garagat's ex-VAT cost per trip, whole AED; null = agreed at payment. */
  costPerTrip?: number | null;
  insuredAmount?: number | null;
  showInsuranceDisclaimer?: boolean;
  disclaimerEn?: string | null;
  disclaimerAr?: string | null;
  /**
   * Spec 080 — the offered zones with their own price; `{}` when none. Optional on the wire for an
   * orders build before Spec 080.
   */
  zonePrices?: ZonePrices;
  updatedBy: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
}

/** `PUT /api/admin/delivery/zofeur-config` — the whole card: a field left out is cleared. */
export interface ZofeurConfigInput {
  price: number;
  threshold: number | null;
  emirates: Emirate[];
  /** #1254. TRN when VAT registered, else null with `vatRegistered: false`. */
  trn: string | null;
  vatRegistered: boolean;
  costPerTrip: number | null;
  insuredAmount: number | null;
  showInsuranceDisclaimer: boolean;
  disclaimerEn: string;
  disclaimerAr: string;
  /**
   * Spec 080 — the card's zone prices, blanks omitted: `{}` clears them. (The server also keeps the
   * stored prices when this is absent, so an older tab cannot wipe them, and drops zones not offered.)
   */
  zonePrices: ZonePrices;
}

/** The server's three 422 sentences (design §3), so a client-side check reads exactly like them. */
export const ZOFEUR_CONFIG_MESSAGES = {
  price: 'Enter a Zofeur price between 1 and 10,000 AED, in whole dirhams',
  threshold: 'Enter a free-above amount between 0 and 1,000,000 AED',
  emirates: 'Choose at least one emirate for Zofeur',
  /** #1254 — the card's provider fields. */
  vat: "Enter Zofeur's TRN or mark Zofeur as not VAT registered",
  trnFormat: 'TRN must be 15 digits',
  cost: 'Enter a cost per trip of 0 AED or more, in whole dirhams',
} as const;

export const MAX_ZOFEUR_PRICE = 10_000;
export const MAX_ZOFEUR_THRESHOLD = 1_000_000;

/**
 * `GET /api/admin/delivery/providers` — soft-deleted providers are never returned (`hiddenAt`).
 *
 * #633: there is no default provider. The admin names the provider for every Garagat leg when
 * assigning its driver, so nothing here marks one row as pre-selected.
 */
export interface RecoveryProvider {
  id: string;
  name: string;
  type: RecoveryProviderType | string;
  /** 15-digit UAE TRN; null when the contractor is explicitly not VAT registered, or IN_HOUSE. */
  trn: string | null;
  vatRegistered: boolean;
  /** Ex-VAT, Garagat's cost. Null = agreed at payment time (provider settlements show trips only). */
  costPerTrip: number | null;
  selfBillingOnFile: boolean;
  /** Only an active provider is offered when a driver is assigned (BR-27). */
  active: boolean;
  updatedAt: string | null;
  /**
   * Spec 072 — the cover while the provider's driver has the customer's car, whole AED. Null = none
   * stated. Optional on the wire so an orders build that predates the fields still parses.
   */
  insuredAmount?: number | null;
  /** Whether the customer sees the disclaimer on legs this provider drives (frozen at assignment). */
  showInsuranceDisclaimer?: boolean;
  /** Disclaimer templates; `{amount}` is filled from `insuredAmount`. */
  disclaimerEn?: string | null;
  disclaimerAr?: string | null;
  /**
   * Spec 072 v2 — always RECOVERY in the registry since #1254 (Zofeur is set up on the Zofeur card);
   * ZOFEUR only on the card's own row, which `listProviders({ includeZofeur: true })` adds.
   */
  transportType?: TransportType | null;
}

/** `POST`/`PUT /api/admin/delivery/providers[/{id}]`. */
/**
 * #633 — a provider as the driver-assignment picker sees it (GET /api/admin/orders/recovery-providers).
 * Only active, non-retired providers are returned, and never their cost, TRN or VAT standing.
 */
export interface AssignableProvider {
  id: string;
  name: string;
  type: RecoveryProviderType | string;
  /** Spec 072 — what the modal needs for the "Customer will see:" preview (and the phone rule). */
  showInsuranceDisclaimer?: boolean;
  insuredAmount?: number | null;
  disclaimerEn?: string | null;
  /**
   * Spec 072 v2 — the picker is already filtered by the order's type. #1254: for ZOFEUR the list is
   * the Zofeur card's row alone (empty until the card is complete).
   */
  transportType?: TransportType | null;
}

export interface RecoveryProviderInput {
  name: string;
  type: RecoveryProviderType;
  trn: string | null;
  vatRegistered: boolean;
  costPerTrip: number | null;
  selfBillingOnFile: boolean;
  active: boolean;
  /** Spec 072. Blank = null; on an edit the server keeps the stored value for a null field. */
  insuredAmount: number | null;
  showInsuranceDisclaimer: boolean;
  disclaimerEn: string;
  disclaimerAr: string;
  // #1254: no transport type — the registry is Recovery only, and the server refuses ZOFEUR.
}

/**
 * Display-only VAT split of a VAT-inclusive figure, rounded per trip the way the backend
 * snapshots it (AC-34: 100.00 → 95.24 + 4.76, and two trips → 190.48 + 9.52 = 2 × each).
 * The server's snapshot is authoritative — this only explains the configured number on screen.
 */
export function splitVatInclusive(gross: number): { net: number; vat: number } {
  if (!Number.isFinite(gross)) return { net: 0, vat: 0 };
  const net = Math.round((gross / (1 + DELIVERY_VAT_RATE)) * 100) / 100;
  return { net, vat: Math.round((gross - net) * 100) / 100 };
}

/** The VAT column on the registry table: TRN when registered, the explicit opt-out otherwise. */
export function providerVatLabel(provider: RecoveryProvider): string {
  if (provider.type === 'IN_HOUSE') return 'Own fleet — no supplier invoice';
  if (provider.vatRegistered) return provider.trn ? `TRN ${provider.trn}` : 'VAT registered';
  return 'Not VAT registered';
}

/**
 * `GET /api/admin/delivery/margin` — Flow F6's month-to-date delivery margin (AC-41).
 *
 * Served by **orders**, because every input is orders' own: the net fee snapshotted on each order,
 * and the cost snapshotted onto each trip at assignment. It rides the `/api/admin/delivery/**`
 * gateway route S9 already added (screen `finance`), so no new route is involved.
 *
 * Unassigned legs are counted, never priced (#633): with no default provider, a trip has no
 * provider — and so no cost — until the admin names one at assignment.
 */
export interface DeliveryMargin {
  /** The Dubai calendar month being reported, `YYYY-MM`. */
  period: string;
  /** First and last day covered, `YYYY-MM-DD`; `to` is today in Dubai. */
  from: string;
  to: string;
  /** Σ net delivery fee snapshotted on this month's orders. */
  feesChargedNet: number;
  /** Garagat legs with a driver assigned this month. */
  assignedTripCount: number;
  /** Σ snapshotted cost of those trips. Trips whose provider had no cost contribute nothing. */
  assignedTripCost: number;
  /** Assigned trips whose provider had no cost configured — why `assignedTripCost` can understate. */
  assignedTripsWithoutCost: number;
  /** Garagat legs on this month's orders still waiting for a driver — counted, not priced. */
  unassignedTripCount: number;
  /** `feesChargedNet − assignedTripCost`. */
  margin: number;
}
